import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  TextField,
  FormControl,
  Tooltip,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  Chip,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Button,
} from "@mui/material";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PercentIcon from "@mui/icons-material/Percent";
import PhoneIcon from "@mui/icons-material/Phone";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { Controller } from "react-hook-form";
import axios from "axios";
import { useSelector } from "react-redux";
import { NoDataOptionField } from "../../../../static/staticdata";
import {
  AccountCircleRounded,
  AddRounded,
  BusinessRounded,
  CheckCircle,
  ErrorOutlineRounded,
  PersonRounded,
} from "@mui/icons-material";
import { useDialog } from "../../../DialogContext";
import {
  appendTeamMemberLawyerId,
  debounce,
  pageScrollStyle,
} from "../../../../styles/styles";
import noOption from "../../../../assets/no-option-found.webp";
import { backend_api } from "../../../../static/server";
import { useUniquenessCheck } from "../../Cases/useUniquenessCheck";

export const getNestedError = (errors, name) =>
  name?.split?.(".").reduce((acc, key) => (acc ? acc[key] : undefined), errors);

export const renderContactOption = (props, option) => (
  <li
    {...props}
    key={option.value}
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography variant="body1">{option.label}</Typography>
    <Box>
      {option?.contact_type === "person" ? (
        <AccountCircleRounded sx={{ fontSize: "0.9rem", color: "#bf3fe1" }} />
      ) : (
        <BusinessRounded sx={{ fontSize: "0.9rem", color: "#2e77f5" }} />
      )}
    </Box>
  </li>
);

export const CustomTextarea = ({
  name,
  placeholder,
  maxLength,
  value,
  onChange,
  onBlur,
  required,
  disabled,
  readonly,
  rows = 2,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        "& textarea": {
          width: "100%",
          height: "2.5rem",
          padding: "8px 12px",
          borderRadius: "4px",
          fontSize: "0.875rem",
          fontFamily: "Roboto, sans-serif",
          border: "1px solid rgba(0, 0, 0, 0.23)",
          outline: "none",
          backgroundColor: disabled ? "#f5f5f5" : "#fff",
          color: disabled ? "rgba(0, 0, 0, 0.6)" : "inherit",
          boxShadow: "0px 0px 1px rgba(0,0,0,0.1)",
          transition: "border 0.3s ease",
          resize: "vertical",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
            "&:hover": {
              background: "#555",
            },
          },
        },
      }}
    >
      <textarea
        id={name}
        name={name}
        placeholder={placeholder}
        maxLength={maxLength}
        value={value || ""}
        onChange={onChange}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
        readOnly={readonly}
        rows={rows}
      />
    </Box>
  );
};

const UNIQUENESS_CONFIG = {
  client_ref: {
    apiUrl: `${backend_api}case/register/`,
    fieldName: "client_ref",
  },
};

const InputFields = ({
  field,
  className,
  width = "100%",
  control,
  errors,
  register,
  isPreview = false,
  onAddButtonClick,
  addButtonText = "Add New",
  showAddButton = false,
  AddFormComponent,
  createDialogTitle = "Create New",
  editOption = null,
  value: externalValue,
  onChange: externalOnChange,
  isSmall = true,
}) => {
  const listRef = useRef(null);
  const { showDialog } = useDialog();
  const [apiOptions, setApiOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { token, teamMemberLawyerId } = useSelector((data) => data.user);
  const [searchText, setSearchText] = useState("");
  const [nextUrl, setNextUrl] = useState(null);
  const [hasLoadedInitially, setHasLoadedInitially] = useState(false); // Track if options have been loaded
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {
    label,
    field_type,
    name,
    placeholder,
    maxLength,
    required,
    tooltip,
    tooltipText,
    readonly,
    disabled,
    pickList = {
      options: [
        { label: "Option 1", value: "option_1" },
        { label: "Option 2", value: "option_2" },
      ],
    },
    rows,
    multiple,
    id,
  } = field || {};

  const fieldUniquenessConfig = UNIQUENESS_CONFIG[name];
  const uniquenessCheck = useUniquenessCheck(
    fieldUniquenessConfig?.apiUrl ?? null,
    token,
    fieldUniquenessConfig?.fieldName ?? null
  );

  const hasApiUrl = pickList?.link_option?.tab_api;
  const defaultLabelPattern = /^option\s*\d+$/i;

  const getOptionDisplayLabel = (option) => {
    if (!option) return "";

    if (typeof option === "string") {
      return option;
    }

    if (option?.isSelfOption || option?.contact_type || hasApiUrl) {
      return option.label || option.value || "";
    }

    const rawLabel = option.label?.toString().trim() || "";
    const optionValue = option.value?.toString().trim() || "";

    if (!rawLabel || defaultLabelPattern.test(rawLabel)) {
      return optionValue || rawLabel;
    }

    return rawLabel;
  };

  // Ensure edit option (for edit forms) is available in the options list
  useEffect(() => {
    if (!editOption) return;

    // Add editOption to apiOptions immediately
    setApiOptions((prev) => {
      const addOpt = (arr, opt) => {
        if (!opt) return arr;
        return arr.some((o) => o.value === opt.value) ? arr : [opt, ...arr];
      };

      let updated = Array.isArray(prev) ? [...prev] : [];

      if (Array.isArray(editOption)) {
        editOption.forEach((opt) => {
          updated = addOpt(updated, opt);
        });
      } else {
        updated = addOpt(updated, editOption);
      }

      return updated;
    });

    // Fetch options only if needed
    if (hasApiUrl && !isPreview && !hasLoadedInitially) {
      fetchOptionsWithSearch();
    }
  }, [editOption, hasApiUrl, isPreview, hasLoadedInitially]);

  // Remove the initial useEffect that loads options on mount
  // We'll load them on focus instead

  const shouldShowSelfOption = useCallback(() => {
    const apiUrl = pickList?.link_option?.tab_api;
    return apiUrl && apiUrl.includes("/team/create-employee/");
  }, [pickList?.link_option?.tab_api]);

  const getSelfOption = () => ({
    label: "Me (Self)",
    value: "self",
    isSelfOption: true,
  });
  const options = useMemo(() => {
    let baseOptions = [];

    if (hasApiUrl && !isPreview && hasLoadedInitially) {
      baseOptions = apiOptions;
    } else if (editOption) {
      // Use editOption as initial options
      baseOptions = Array.isArray(editOption) ? [...editOption] : [editOption];
    } else {
      baseOptions = pickList?.options || [];
    }

    // Add "Me (Self)" option if needed
    if (shouldShowSelfOption()) {
      return [getSelfOption(), ...baseOptions];
    }

    return baseOptions;
  }, [
    hasApiUrl,
    isPreview,
    hasLoadedInitially,
    apiOptions,
    pickList?.options,
    editOption,
    shouldShowSelfOption,
  ]);

  const useColorChips = !!pickList?.useColors;

  const formatApiResponse = (data) => {
    if (data?.api && data?.tabName === "contact") {
      if (Array.isArray(data?.res)) {
        return data?.res?.map((item) => {
          let label = "-";
          if (
            item?.contact_type === "person" &&
            (item?.first_name || item?.last_name)
          ) {
            const firstName = item?.first_name || "";
            const lastName = item?.last_name || "";
            label = `${firstName} ${lastName}`.trim();
          } else if (item?.contact_type === "company" && item?.company_name) {
            label = item?.company_name;
          } else if (
            item?.contact_type === "company" &&
            (item?.name || item?.business_name || item?.organization_name)
          ) {
            label =
              item?.name || item?.business_name || item?.organization_name;
          } else if (item?.first_name || item?.last_name) {
            const firstName = item?.first_name || "";
            const lastName = item?.last_name || "";
            label = `${firstName} ${lastName}`.trim();
          } else if (item?.company_name || item?.name) {
            label = item?.company_name || item?.name;
          }

          return {
            label: label || "-",
            contact_type: item?.contact_type ?? "unknown",
            value: item?.id.toString(),
          };
        });
      }
    } else if (data?.api && data?.tabName === "member") {
      if (Array.isArray(data?.res)) {
        return data?.res?.map((item) => {
          let label = "-";
          if (item?.first_name || item?.last_name) {
            const firstName = item?.first_name || "";
            const lastName = item?.last_name || "";
            label = `${firstName} ${lastName}`.trim();
          }

          return {
            label: label || "-",
            value: item?.id.toString(),
          };
        });
      }
    }

    return [];
  };

  const getSearchKeyForField = (fieldName, pickListConfig) => {
    if (pickListConfig?.link_option?.search_key) {
      return pickListConfig.link_option.search_key;
    }

    const apiUrl = pickListConfig?.link_option?.tab_api?.toLowerCase() || "";
    if (apiUrl.includes("/contact/")) return "name";
    if (apiUrl.includes("/case/") || apiUrl.includes("/matter/"))
      return "client_ref";
    if (apiUrl.includes("/task/")) return "name";
    if (apiUrl.includes("/employee/") || apiUrl.includes("/member/"))
      return "name";
    if (apiUrl.includes("/category/")) return "category_name";

    const fieldLower = (fieldName || "").toLowerCase();
    if (fieldLower.includes("client")) return "name";
    if (fieldLower.includes("company")) return "company_name";
    if (fieldLower.includes("contact")) return "contact_name";
    if (fieldLower.includes("matter") || fieldLower.includes("case"))
      return "case_name";
    if (fieldLower === "client_ref") return "client_ref";

    return "search";
  };

  const fetchOptionsWithSearch = async (
    searchQuery = "",
    isLoadMore = false
  ) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const dynamicSearchKey = getSearchKeyForField(name, pickList);

      let url;

      if (isLoadMore && nextUrl) {
        url = nextUrl;
      } else {
        const baseUrl = appendTeamMemberLawyerId(
          pickList.link_option.tab_api,
          teamMemberLawyerId
        );
        const urlObj = new URL(baseUrl);

        if (searchQuery && searchQuery.trim()) {
          urlObj.searchParams.set(dynamicSearchKey, searchQuery.trim());
        }

        url = urlObj.toString();
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Token ${token}` },
      });

      const formattedOptions = formatApiResponse({
        res: response?.data?.results || [],
        tabName: pickList?.link_option?.tab,
        api: pickList?.link_option?.tab_api,
      });

      const safeFormatted = Array.isArray(formattedOptions)
        ? formattedOptions
        : [];

      if (isLoadMore) {
        setApiOptions((prev) => [...prev, ...safeFormatted]);
      } else {
        setApiOptions(safeFormatted);
        setHasLoadedInitially(true);
      }

      // Update next page URL
      setNextUrl(response?.data?.next || null);
    } catch (error) {
      console.error("Error fetching picklist options:", error);
      if (!isLoadMore) {
        setApiOptions([]);
        setHasLoadedInitially(true);
      }
    } finally {
      setIsLoading(false);
    }
  };
  // Load options on focus/click
  const handleFocus = () => {
    if (hasApiUrl && !isPreview) {
      if (!hasLoadedInitially || apiOptions.length === 0) {
        fetchOptionsWithSearch("", false);
      }
    }
  };

  // Handle dropdown open/close
  const handleDropdownOpen = () => {
    setIsDropdownOpen(true);
    if (hasApiUrl && !isPreview && !hasLoadedInitially) {
      fetchOptionsWithSearch("", false);
    }
  };

  // const refreshOptions = () => {
  //   if (hasApiUrl && !isPreview) {
  //     setHasLoadedInitially(false);
  //     setApiOptions([]); // Clear existing options
  //     fetchOptionsWithSearch(); // Fetch fresh data
  //   }
  // };

  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
  };

  // Create debounced search function
  const debounceSearch = useCallback(
    debounce((searchQuery) => {
      if (hasApiUrl && !isPreview && hasLoadedInitially) {
        fetchOptionsWithSearch(searchQuery, false);
      }
    }, 500),
    [hasApiUrl, isPreview, hasLoadedInitially, name, pickList]
  );

  const fieldId = id;
  const isMultiSelect = multiple || pickList?.multiSelect;

  const handleScroll = async (e) => {
    if (isPreview || !hasApiUrl) return;

    const listNode = e.currentTarget;
    if (!listNode) return;
    const { scrollTop, scrollHeight, clientHeight } = listNode;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isNearBottom = distanceFromBottom <= 50;

    if (isNearBottom && !isLoading && nextUrl) {
      // console.log("Loading more options...", { nextUrl });
      await fetchOptionsWithSearch(searchText, true);
    }
  };

  const handleCreateNew = () => {
    if (AddFormComponent) {
      showDialog(<AddFormComponent />, {
        dialogTitle: createDialogTitle,
      });
    } else if (onAddButtonClick) {
      onAddButtonClick();
    }
  };

  const getCurrentSelectedOptions = (fieldValue) => {
    if (!fieldValue) {
      if (!isMultiSelect && shouldShowSelfOption()) {
        return getSelfOption();
      }
      return isMultiSelect ? [] : null;
    }

    const findOption = (val) => {
      if (val === "self") return getSelfOption();
      const matchingOption = options.find((opt) => opt.value === val);
      if (matchingOption) return matchingOption;

      if (Array.isArray(editOption)) {
        const matchEdit = editOption.find((opt) => opt.value === val);
        if (matchEdit) return matchEdit;
      } else if (editOption && editOption.value === val) {
        return editOption;
      }

      return { label: val, value: val };
    };

    if (isMultiSelect) {
      if (!Array.isArray(fieldValue)) return [];
      return fieldValue.map((val) =>
        typeof val === "object" ? val : findOption(val)
      );
    }

    return typeof fieldValue === "object" ? fieldValue : findOption(fieldValue);
  };

  const renderSelfOption = (props, option) => (
    <li {...props} key={option.value}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "primary.main" }}
          >
            {option.label}
          </Typography>
        </Box>
      </Box>
    </li>
  );

  // Adornment helpers
  const getStartAdornment = () => {
    if (field_type === "email") {
      return (
        <EmailRoundedIcon
          sx={{ fontSize: "1.2rem", color: "text.secondary" }}
        />
      );
    } else if (field_type === "phone") {
      return <PhoneIcon sx={{ fontSize: "1.2rem", color: "text.secondary" }} />;
    }
    return null;
  };

  const getEndAdornment = () => {
    if (field_type === "currency") {
      return (
        <AttachMoneyIcon sx={{ fontSize: "1.2rem", color: "text.secondary" }} />
      );
    } else if (field_type === "percent") {
      return (
        <PercentIcon sx={{ fontSize: "1.2rem", color: "text.secondary" }} />
      );
    }
    return null;
  };

  const ListboxComponent = (props) => {
    const { children, ...rest } = props;

    const handleButtonClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleCreateNew();
    };

    return (
      <Box sx={{ position: "relative" }}>
        <Box {...rest} sx={{ overflowY: "auto", maxHeight: "300px" }}>
          {children}
        </Box>
        {hasApiUrl &&
          !isPreview &&
          showAddButton &&
          (onAddButtonClick || AddFormComponent) && (
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                borderTop: "1px solid #e5e5e5",
                backgroundColor: "white",
                p: 1,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={handleButtonClick}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                variant="text"
                size="small"
                color="primary"
                endIcon={<AddRounded />}
                sx={{
                  pointerEvents: "auto",
                  zIndex: 1,
                }}
              >
                {addButtonText}
              </Button>
            </Box>
          )}
      </Box>
    );
  };

  const hasStartAdornment = !!getStartAdornment();
  const hasEndAdornment = !!getEndAdornment();

  // Common props for input fields
  const getCommonInputProps = () => ({
    id: fieldId,
    name: name,
    placeholder,
    required,
    disabled: disabled || isPreview,
    size: isSmall ? "small" : "medium",
    fullWidth: true,
    error: !!errors?.[name],
    helperText: errors?.[name]?.message,
    InputProps: {
      readOnly: readonly || isPreview,
      ...(hasStartAdornment && {
        startAdornment: (
          <InputAdornment
            position="start"
            sx={{
              ml: "-0.9rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: "0.5rem",
              borderRight: "1px solid #e0e0e0",
            }}
          >
            {getStartAdornment()}
          </InputAdornment>
        ),
      }),
      ...(hasEndAdornment && {
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: "0.5rem",
              borderLeft: "1px solid #e0e0e0",
              mr: "-0.9rem",
            }}
          >
            {getEndAdornment()}
          </InputAdornment>
        ),
      }),
    },
  });

  // Validation rules
  const getValidationRules = (fieldType) => {
    const commonRules = {
      required: required && !isPreview ? "This field is required" : false,
    };

    if (isPreview) return {}; // No validation in preview mode

    const rules = { ...commonRules };
    switch (fieldType) {
      case "email":
        rules.pattern = {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address",
        };
        break;
      case "phone":
        rules.pattern = {
          value: /^[0-9+-\s()]*$/,
          message: "Invalid phone number",
        };
        break;
      case "number":
      case "decimal":
      case "long_integer":
      case "currency":
        return {
          ...commonRules,
          validate: (value) => {
            if (value === "" || value === null) return true;
            return !isNaN(value) || "Must be a valid number";
          },
        };
        break;
      case "percent":
        rules.pattern = {
          value: /^-?\d*\.?\d*$/,
          message: "Must be a valid number",
        };
        rules.max = {
          value: 100,
          message: "Percentage cannot exceed 100%",
        };
        rules.min = {
          value: 0,
          message: "Percentage cannot be negative",
        };
        break;
    }

    return rules;
  };

  // Get default value based on field type
  const getDefaultValue = () => {
    if (isPreview) {
      switch (field_type) {
        case "check_box":
          return false;
        case "pick_list":
          if (shouldShowSelfOption()) {
            return isMultiSelect ? ["self"] : "self";
          }
          return isMultiSelect ? [] : "";
        case "date":
        case "date_time":
          return null;
        default:
          return "";
      }
    }

    switch (field_type) {
      case "check_box":
        return false;
      case "pick_list":
        if (shouldShowSelfOption()) {
          return isMultiSelect ? [] : null;
        }
        return isMultiSelect ? [] : "";
      case "date":
      case "date_time":
        return null;
      default:
        return "";
    }
  };

  // Render field component
  const renderField = () => {
    const commonRules = getValidationRules(field_type);
    const defaultValue = getDefaultValue();

    const resolveValue = (hookFormValue) =>
      externalValue !== undefined ? externalValue : hookFormValue;

    const handleExternalChange = (val) => {
      if (externalOnChange) {
        externalOnChange(val);
      }
    };

    switch (field_type) {
      case "single_line":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={{
              ...commonRules,
              validate: (value) => {
                if (
                  fieldUniquenessConfig &&
                  !uniquenessCheck.isUnique &&
                  value
                ) {
                  return uniquenessCheck.errorMessage;
                }
                return true;
              },
            }}
            render={({ field: hookFormField }) => {
              const handleChange = (e) => {
                const value = e.target.value;
                hookFormField.onChange(e);
                handleExternalChange(value);
                if (fieldUniquenessConfig) {
                  uniquenessCheck.checkUniqueness(value);
                }
              };

              const hasUniquenessError =
                fieldUniquenessConfig &&
                !uniquenessCheck.isUnique &&
                hookFormField.value;

              const isFieldError = !!errors?.[name] || hasUniquenessError;

              const helperText =
                errors?.[name]?.message ||
                (hasUniquenessError ? uniquenessCheck.errorMessage : "") ||
                (fieldUniquenessConfig &&
                uniquenessCheck.isChecking &&
                hookFormField.value
                  ? "Checking availability..."
                  : "");

              return (
                <TextField
                  {...hookFormField}
                  {...getCommonInputProps()}
                  type="text"
                  inputProps={{ maxLength: field.characterLimit || maxLength }}
                  variant="outlined"
                  onChange={handleChange}
                  value={resolveValue(hookFormField.value) || ""}
                  error={isFieldError}
                  helperText={helperText}
                  InputProps={{
                    ...getCommonInputProps().InputProps,
                    endAdornment: (
                      <>
                        {fieldUniquenessConfig &&
                          uniquenessCheck.isChecking && (
                            <InputAdornment position="end">
                              <CircularProgress size={16} />
                            </InputAdornment>
                          )}

                        {fieldUniquenessConfig &&
                          !uniquenessCheck.isChecking &&
                          !uniquenessCheck.isUnique &&
                          hookFormField.value && (
                            <InputAdornment position="end">
                              <Tooltip title={uniquenessCheck.errorMessage}>
                                <ErrorOutlineRounded
                                  color="error"
                                  fontSize="small"
                                />
                              </Tooltip>
                            </InputAdornment>
                          )}

                        {fieldUniquenessConfig &&
                          !uniquenessCheck.isChecking &&
                          uniquenessCheck.isUnique &&
                          hookFormField.value && (
                            <InputAdornment position="end">
                              <CheckCircle color="success" fontSize="small" />
                            </InputAdornment>
                          )}
                        {getCommonInputProps().InputProps?.endAdornment}
                      </>
                    ),
                  }}
                />
              );
            }}
          />
        );

      case "multi_line":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={commonRules}
            render={({ field: hookFormField }) => (
              <CustomTextarea
                {...hookFormField}
                name={fieldId}
                value={resolveValue(hookFormField.value) || ""}
                onChange={(e) => {
                  hookFormField.onChange(e);
                  handleExternalChange(e.target.value);
                }}
                placeholder={placeholder}
                maxLength={field.characterLimit || maxLength}
                required={required}
                disabled={disabled || isPreview}
                readonly={readonly || isPreview}
                rows={rows || 2}
              />
            )}
          />
        );

      case "email":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={commonRules}
            render={({ field: hookFormField }) => (
              <TextField
                {...hookFormField}
                {...getCommonInputProps()}
                type="email"
                inputProps={{ maxLength }}
                variant="outlined"
                value={resolveValue(hookFormField.value) || ""}
                onChange={(e) => {
                  hookFormField.onChange(e);
                  handleExternalChange(e.target.value);
                }}
              />
            )}
          />
        );

      case "phone":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={commonRules}
            render={({ field: hookFormField }) => (
              <TextField
                {...hookFormField}
                {...getCommonInputProps()}
                type="tel"
                inputProps={{ maxLength }}
                variant="outlined"
                value={resolveValue(hookFormField.value) || ""}
                onChange={(e) => {
                  hookFormField.onChange(e);
                  handleExternalChange(e.target.value);
                }}
              />
            )}
          />
        );

      case "pick_list":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={getDefaultValue()}
            rules={commonRules}
            render={({ field: hookFormField }) => (
              <Autocomplete
                {...hookFormField}
                multiple={isMultiSelect}
                options={options}
                loading={isLoading && !isPreview}
                disabled={disabled || isPreview}
                readOnly={readonly || isPreview}
                open={isDropdownOpen}
                onOpen={handleDropdownOpen}
                onClose={handleDropdownClose}
                getOptionLabel={(option) => getOptionDisplayLabel(option)}
                isOptionEqualToValue={(option, val) => {
                  if (typeof option === "object" && typeof val === "object") {
                    return option.value === val.value;
                  }
                  if (typeof option === "object" && typeof val === "string") {
                    return option.value === val;
                  }
                  return option === val;
                }}
                inputValue={searchText}
                onInputChange={(_, value) => {
                  setSearchText(value || "");
                  if (hasApiUrl && !isPreview && hasLoadedInitially) {
                    debounceSearch(value || "");
                  }
                }}
                ListboxProps={{
                  onScroll: handleScroll,
                  ref: listRef,
                  style: { maxHeight: "300px" },
                  sx: pageScrollStyle,
                }}
                ListboxComponent={ListboxComponent}
                noOptionsText={
                  !hasLoadedInitially && hasApiUrl && !isPreview ? (
                    <Box sx={{ textAlign: "center", py: 2 }}>
                      <Typography variant="body2">
                        Click to load options...
                      </Typography>
                    </Box>
                  ) : (
                    <NoDataOptionField
                      imgSrc={noOption}
                      text={"Ooops there is not Data to show"}
                      btnText={addButtonText}
                      handleClickNoData={handleCreateNew}
                    />
                  )
                }
                disablePortal={false}
                PopperProps={{
                  container: document.body,
                  sx: { zIndex: 14000 },
                }}
                renderInput={(params) => {
                  const selected =
                    !isMultiSelect &&
                    getCurrentSelectedOptions(hookFormField.value);

                  return (
                    <TextField
                      {...params}
                      placeholder={placeholder}
                      size="small"
                      required={required}
                      error={!!errors?.[name]}
                      helperText={errors?.[name]?.message}
                      onFocus={handleFocus}
                      onClick={handleFocus}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment:
                          !isMultiSelect && useColorChips && selected ? (
                            <Chip
                              size="small"
                              label={getOptionDisplayLabel(selected)}
                              sx={{
                                bgcolor: selected.color || "primary.main",
                                color: "#fff",
                                mr: 0.5,
                              }}
                            />
                          ) : (
                            params.InputProps.startAdornment
                          ),
                        endAdornment: (
                          <>
                            {isLoading && !isPreview ? (
                              <CircularProgress color="primary" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  );
                }}
                renderOption={(props, option, state) => {
                  if (option?.isSelfOption) {
                    return renderSelfOption(props, option);
                  } else if (
                    option?.contact_type &&
                    option.contact_type !== "self"
                  ) {
                    return renderContactOption(props, option);
                  } else {
                    return (
                      <li {...props} key={option.value}>
                        {getOptionDisplayLabel(option)}
                        {option?.isPlaceholder && (
                          <Typography
                            variant="caption"
                            sx={{ ml: 1, color: "text.secondary" }}
                          >
                            (Current Selection)
                          </Typography>
                        )}
                      </li>
                    );
                  }
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue?.map((option, index) => {
                    const baseProps = getTagProps({ index });
                    const chipStyle =
                      useColorChips && option?.color
                        ? { bgcolor: option.color, color: "#fff" }
                        : {};

                    const chipColor = option?.isSelfOption
                      ? "primary"
                      : option?.isPlaceholder
                      ? "default"
                      : useColorChips && option?.color
                      ? undefined
                      : "primary";

                    return (
                      <Chip
                        label={getOptionDisplayLabel(option)}
                        size="small"
                        {...baseProps}
                        key={`chip-${index}`}
                        color={chipColor}
                        sx={chipStyle}
                        icon={
                          option?.isSelfOption ? (
                            <PersonRounded sx={{ fontSize: "0.9rem" }} />
                          ) : undefined
                        }
                      />
                    );
                  })
                }
                onChange={(_, newValue) => {
                  if (isPreview) return; // Don't handle changes in preview mode

                  if (isMultiSelect) {
                    const values = newValue
                      .map((item) =>
                        typeof item === "object" ? item.value : item
                      )
                      .filter((val) => val !== "self");
                    hookFormField.onChange(values);
                    handleExternalChange(values);
                  } else {
                    let value = newValue
                      ? typeof newValue === "object"
                        ? newValue.value
                        : newValue
                      : "";
                    if (value === "self") {
                      value = null;
                    }
                    hookFormField.onChange(value);
                    handleExternalChange(value);
                  }
                }}
                value={getCurrentSelectedOptions(
                  resolveValue(hookFormField.value)
                )}
              />
            )}
          />
        );

      case "date":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={commonRules}
            render={({ field: hookFormField }) => (
              <DatePicker
                {...hookFormField}
                value={resolveValue(hookFormField.value) || null}
                onChange={(date) => {
                  if (isPreview) return;
                  hookFormField.onChange(date);
                  handleExternalChange(date);
                }}
                disabled={disabled || isPreview}
                readOnly={readonly || isPreview}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    required: required,
                    error: !!errors?.[name],
                    helperText: errors?.[name]?.message,
                  },
                  popper: {
                    sx: {
                      zIndex: 1600,
                    },
                  },
                }}
                format="MM/DD/YYYY"
              />
            )}
          />
        );

      case "date_time":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={commonRules}
            render={({ field: hookFormField }) => (
              <DateTimePicker
                {...hookFormField}
                value={resolveValue(hookFormField.value) || null}
                onChange={(date) => {
                  if (isPreview) return;
                  hookFormField.onChange(date);
                  handleExternalChange(date);
                }}
                disabled={disabled || isPreview}
                readOnly={readonly || isPreview}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    required: required,
                    error: !!errors?.[name],
                    helperText: errors?.[name]?.message,
                  },
                  popper: {
                    sx: {
                      zIndex: 1600,
                    },
                  },
                }}
                format="MM/DD/YYYY hh:mm A"
              />
            )}
          />
        );

      case "number":
      case "decimal":
      case "long_integer":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={commonRules}
            render={({ field: hookFormField }) => (
              <TextField
                {...hookFormField}
                {...getCommonInputProps()}
                type="text"
                inputProps={{
                  maxLength,
                  step: field_type === "decimal" ? "0.01" : "1",
                  onInput: (e) => {
                    e.target.value = e.target.value.replace(/[^0-9.-]/g, "");

                    // Ensure only one decimal point
                    if ((e.target.value.match(/\./g) || []).length > 1) {
                      e.target.value = e.target.value.slice(0, -1);
                    }
                  },
                }}
                variant="outlined"
                value={resolveValue(hookFormField.value) || ""}
                onChange={(e) => {
                  hookFormField.onChange(e);
                  handleExternalChange(e.target.value);
                }}
              />
            )}
          />
        );

      case "currency":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={commonRules}
            render={({ field: hookFormField }) => (
              <TextField
                {...hookFormField}
                {...getCommonInputProps()}
                type="number"
                inputProps={{
                  maxLength,
                  step: "0.01",
                }}
                variant="outlined"
              />
            )}
          />
        );

      case "percent":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={commonRules}
            render={({ field: hookFormField }) => (
              <TextField
                {...hookFormField}
                {...getCommonInputProps()}
                type="number"
                inputProps={{
                  maxLength,
                  step: "0.01",
                  min: "0",
                  max: "100",
                }}
                variant="outlined"
              />
            )}
          />
        );

      case "check_box":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={commonRules}
            render={({ field: hookFormField }) => (
              <FormControl
                fullWidth
                error={!!errors?.[name]}
                sx={{
                  border: "1px solid #ddd",
                  mt: "0.45rem",
                  borderRadius: "0.2rem",
                  pl: "1rem",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      {...hookFormField}
                      checked={!!hookFormField.value}
                      disabled={disabled || isPreview}
                      readOnly={readonly || isPreview}
                      size="small"
                      onChange={(e) =>
                        !isPreview && hookFormField.onChange(e.target.checked)
                      }
                    />
                  }
                  label={placeholder || label || ""}
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.8rem",
                      fontWeight: 400,
                      color:
                        disabled || isPreview
                          ? "text.disabled"
                          : "text.primary",
                    },
                  }}
                />
                {errors?.[name] && (
                  <Typography color="error" variant="caption">
                    {errors[name].message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        );

      default:
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={commonRules}
            render={({ field: hookFormField }) => (
              <TextField
                {...hookFormField}
                {...getCommonInputProps()}
                type={field_type || "text"}
                inputProps={{ maxLength }}
                variant="outlined"
              />
            )}
          />
        );
    }
  };

  // Special handling for checkbox field
  if (field_type === "check_box") {
    return (
      <Box className={className} sx={{ width, mt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {renderField()}
          {tooltip && (
            <Tooltip title={tooltipText || ""} arrow placement="top">
              <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                <HelpRoundedIcon sx={{ fontSize: "1rem" }} color="primary" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  }

  if (!field) return null;

  // Standard field with label
  return (
    <Box className={className} sx={{ width }}>
      <FormControl fullWidth variant="outlined" size="medium">
        {label && (
          <Box sx={{ display: "flex", alignItems: "center", mb: "0.2rem" }}>
            <Typography
              variant="body1"
              component="label"
              htmlFor={fieldId}
              sx={{ fontSize: "0.8rem", fontWeight: 500 }}
            >
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </Typography>

            {tooltip && (
              <Tooltip title={tooltipText || ""} arrow placement="top">
                <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                  <HelpRoundedIcon sx={{ fontSize: "1rem" }} color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
          {renderField()}

          {required && !isPreview && (
            <Box
              sx={{
                position: "absolute",
                top: "0rem",
                right: "0rem",
                width: "0",
                height: "0",
                borderRight: "12px solid #dc3545",
                borderBottom: "12px solid transparent",
                zIndex: 1,
              }}
            />
          )}
        </Box>
      </FormControl>
    </Box>
  );
};

export default InputFields;
